/** @format */
import React, { FC, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  Datepicker,
  Timepicker,
  Drawer,
  _$,
  Form,
  FormItem,
  Input,
  Radio,
  Checkbox,
  Message
} from '@king-design/react'
import { ProductGroupSelect } from 'src/components'
import { ProductItem } from 'src/utils/interface'
import useGlobalData from 'src/hooks/useGlobalData'
import SelectInstance from 'src/components/SelectInstance'
import SelectMetricsTags, { initShieldMetrics } from 'src/components/SelectMetricsTags'
import SelectEventTags, { initEvents } from 'src/components/SelectEventTags'
import { createShields, modifyShields, getShieldsInstances } from 'src/api/ApiAlarmSilences'
import { useAsync } from 'react-use'
import { handleParamsCase } from 'src/utils/common'
import { cloneDeep } from 'lodash'
import ApiCommon from 'src/api/ApiCommon'
import { request } from 'src/utils'
import dayjs from 'dayjs'

interface EditDrawerProps {
  className?: string
  visible: boolean
  eventList: any[]
  editData?: any
  onClose: (update?: boolean) => void
}

const initFormValues = {
  ShieldName: '',
  ShieldDescription: '',
  ProductType: undefined,
  ShieldType: 'instance',
  Instances: [],
  ShieldRange: ['threshold', 'event'],
  ShieldMetrics: initShieldMetrics,
  ShieldEvents: initEvents,
  ShieldTimeType: 1,
  AbsoluteDate: [],
  // Date: [],
  Time: []
}

export const ShieldTypeOpts = {
  instance: '实例屏蔽'
}

export const ShieldTimeTypeOpts = {
  1: '永久生效',
  2: '绝对时间范围',
  3: '相对时间范围(每天循环)'
}

const EditDrawer: FC<EditDrawerProps> = ({ className, visible, editData, onClose, eventList }) => {
  const productList = useGlobalData('productList')
  // form表单
  const formRef = useRef(null)
  const [formValues, setFormValues] = useState<any>(initFormValues)
  const [curProduct, setCurProduct] = useState<ProductItem>()
  // 全部指标/事件 1=全部、2=指定
  const [allMetric, setAllMetric] = useState(1)
  const [allEvent, setAllEvent] = useState(1)
  // 临时存储mertric，解决修改instaces同时过滤多个指标的tags的缓存问题
  const metricsRef = useRef<any[]>([])

  useAsync(async () => {
    if (visible) {
      if (editData?.ShieldID) {
        const data = editData
        const instancesResult = await getShieldsInstances(data?.ShieldID)
        const {
          ProductType,
          // EffectStartDate,
          // EffectEndDate,
          EffectStartTime,
          EffectEndTime,
          IsEventFlag,
          IsThresholdFlag
        } = data
        const ShieldRange = []
        if (IsThresholdFlag === 1) {
          ShieldRange.push('threshold')
        }
        if (IsEventFlag === 1) {
          ShieldRange.push('event')
        }
        const values = Object.assign(
          {
            ShieldType: 'instance',
            ShieldRange
          },
          data,
          {
            EffectStartTime: undefined,
            EffectEndTime: undefined,
            Instances: undefined
          }
        )
        values.Instances = handleParamsCase(instancesResult?.Instances || [], 'lower')
        // 全部指标/事件 1=全部、2=指定
        if (values.IsThresholdFlag === 1) {
          if (values.ShieldMetrics?.length) {
            setAllMetric(2)
          } else {
            setAllMetric(1)
          }
        }
        if (values.IsEventFlag === 1) {
          if (values.ShieldEvents?.length) {
            setAllEvent(2)
          } else {
            setAllEvent(1)
          }
        }
        if (!values.ShieldMetrics?.length) {
          values.ShieldMetrics = initShieldMetrics
        }
        if (!values.ShieldEvents?.length) {
          values.ShieldEvents = initEvents
        }
        if (values.ShieldTimeType === 2) {
          values.AbsoluteDate =
            EffectStartTime && EffectEndTime ? [EffectStartTime, EffectEndTime] : []
        }
        if (values.ShieldTimeType === 3) {
          // values.Date = EffectStartDate && EffectEndDate ? [EffectStartDate, EffectEndDate] : []
          values.Time = EffectStartTime && EffectEndTime ? [EffectStartTime, EffectEndTime] : []
        }
        metricsRef.current = values.ShieldMetrics
        const cur = findProduct(ProductType)
        setCurProduct(cur)
        setFormValues(values)
      }
    } else {
      metricsRef.current = []
    }
  }, [visible, editData])
  // 修改表单
  const onFormChange = (values: any) => {
    setFormValues({
      ...formValues,
      ...values
    })
  }

  // 获取产品线下的监控项
  const { value: metricList } = useAsync(async () => {
    if (formValues.ProductType !== undefined && formValues.ProductType !== null) {
      const { data } = await request(ApiCommon.getProductMetricList, {
        data: { ProductType: formValues.ProductType, PageIndex: 1 }
      })
      return data?.metricList || []
    } else {
      return []
    }
  }, [formValues.ProductType])

  // 获取产品线数据
  const findProduct = productType => {
    return productList?.find(item => item.productType === parseFloat(productType))
  }

  // 切换时间生效范围
  const onTimeRangeChange = (value: any) => {
    onFormChange({
      ShieldTimeType: value,
      AbsoluteDate: [],
      Date: [],
      Time: []
    })
  }

  // 提交
  const formSubmit = async () => {
    const validate = await formRef?.current?.validate()
    if (validate) {
      const {
        Time,
        AbsoluteDate,
        Instances,
        ShieldRange,
        ProductType,
        ShieldName,
        ShieldID,
        ShieldTimeType,
        ShieldDescription,
        ShieldType,
        ShieldMetrics,
        ShieldEvents
      } = formValues
      const params: any = {
        ShieldID,
        ShieldName,
        ShieldDescription,
        ProductType,
        ShieldType,
        ShieldInstances: Instances.map(item => item.instanceId),
        IsThresholdFlag: ShieldRange.includes('threshold') ? 1 : 2,
        IsEventFlag: ShieldRange.includes('event') ? 1 : 2,
        ShieldTimeType
      }
      // 是否有指标
      params.ShieldMetrics =
        params.IsThresholdFlag === 2 || allMetric === 1
          ? []
          : ShieldMetrics.filter(({ Metric }) => !!Metric)
      // 是否有事件
      params.ShieldEvents =
        params.IsEventFlag === 2 || allEvent === 1
          ? []
          : ShieldEvents.filter(({ EventName }) => !!EventName)
      // 屏蔽时间范围
      if (params.ShieldTimeType === 2) {
        params.EffectStartTime = AbsoluteDate[0]
        params.EffectEndTime = AbsoluteDate[1]
      }
      if (params.ShieldTimeType === 3) {
        // params.EffectStartDate = Date[0]
        // params.EffectEndDate = Date[1]
        params.EffectStartTime = Time[0]
        params.EffectEndTime = Time[1]
      }
      const result = editData ? await modifyShields(params) : await createShields(params)
      if (result) {
        Message.success(ShieldID ? _$('修改成功') : _$('创建成功'))
        setFormValues(initFormValues)
        onClose(true)
      }
    }
  }
  return (
    <Drawer
      title={editData?.ShieldID ? _$('修改屏蔽规则') : _$('新建屏蔽规则')}
      value={visible}
      onClose={() => {
        setFormValues(initFormValues)
        onClose()
      }}
      ok={formSubmit}
      width={850}
    >
      <div className={className}>
        <Form starOnRequired ref={formRef} labelWidth={100}>
          <FormItem
            label={_$('规则名称')}
            value={formValues.ShieldName}
            rules={{ required: true, userName: true }}
            messages={{
              required: _$('请输入规则名称')
            }}
          >
            <Input
              value={formValues.ShieldName}
              width={500}
              placeholder={_$('请输入规则名称')}
              onChangeValue={value => onFormChange({ ShieldName: value })}
            ></Input>
          </FormItem>
          <FormItem label={_$('描述')} value={formValues.ShieldDescription}>
            <Input
              type="textarea"
              rows="5"
              resize="none"
              value={formValues.ShieldDescription}
              width={500}
              placeholder={_$('请输入描述')}
              onChangeValue={value => onFormChange({ ShieldDescription: value })}
            ></Input>
          </FormItem>
          <FormItem
            label={_$('产品类型')}
            value={formValues?.ProductType}
            rules={{ required: true }}
            messages={{
              required: _$('请选择产品类型')
            }}
          >
            <ProductGroupSelect
              disabled={!!editData?.ShieldID}
              value={formValues?.ProductType}
              width={500}
              onChange={value => {
                if (formValues?.ProductType !== value) {
                  const cur = findProduct(value)
                  setCurProduct(cur)
                  onFormChange({
                    ProductType: value,
                    Instances: [],
                    ShieldRange: [],
                    ShieldMetrics: initShieldMetrics,
                    ShieldEvents: initEvents
                  })
                }
              }}
            />
          </FormItem>
          <FormItem
            label={_$('屏蔽类型')}
            value={formValues.ShieldType}
            rules={{ required: true }}
            messages={{
              required: _$('请选择屏蔽类型')
            }}
          >
            {Object.keys(ShieldTypeOpts).map(key => {
              const label = ShieldTypeOpts[key]
              return (
                <Radio
                  name="shieldType"
                  trueValue={key}
                  key={key}
                  value={formValues.ShieldType}
                  onChangeValue={value => {
                    onFormChange({
                      ShieldType: value,
                      Instances: []
                    })
                  }}
                >
                  {_$(label)}
                </Radio>
              )
            })}
          </FormItem>
          {formValues.ProductType !== undefined ? (
            <>
              <FormItem
                label={_$('屏蔽对象')}
                value={formValues.Instances}
                rules={{ required: true }}
                messages={{
                  required: _$('请选择屏蔽对象')
                }}
              >
                <SelectInstance
                  productType={formValues.ProductType + ''}
                  allInstance={false}
                  projectDimens={false}
                  instanceGroup={false}
                  isGroupFilter={true}
                  isRegionFilter={true}
                  defaultRadioValue={3}
                  defaultInstance={formValues.Instances || []}
                  batchSearch={true}
                  instanceMaxLength={1000}
                  onChange={(redio, value) => {
                    if (redio === 3) {
                      onFormChange({
                        Instances: value as any
                      })
                    }
                  }}
                />
              </FormItem>
              <FormItem
                label={_$('屏蔽范围')}
                value={formValues.ShieldRange}
                rules={{ required: true }}
                messages={{
                  required: _$('请选择屏蔽范围')
                }}
              >
                {curProduct?.thresholdFlag === 1 ? (
                  <Checkbox
                    name="range"
                    trueValue="threshold"
                    value={formValues.ShieldRange}
                    onChangeValue={value => {
                      onFormChange({
                        ShieldRange: value
                      })
                    }}
                  >
                    {_$('指标')}
                  </Checkbox>
                ) : null}
                {curProduct?.sysEventFlag === 1 ? (
                  <Checkbox
                    name="range"
                    trueValue="event"
                    value={formValues.ShieldRange}
                    onChangeValue={value => {
                      onFormChange({
                        ShieldRange: value
                      })
                    }}
                  >
                    {_$('事件')}
                  </Checkbox>
                ) : null}
              </FormItem>
              {formValues.ShieldRange?.includes('threshold') ? (
                <div className="form_item_metrics">
                  <FormItem className="form_item_radio" label={_$('指标')}>
                    <Radio
                      name="allMetric"
                      trueValue={1}
                      value={allMetric}
                      onChangeValue={value => {
                        setAllMetric(value)
                      }}
                    >
                      {_$('全部指标')}
                    </Radio>
                    <Radio
                      name="allMetric"
                      trueValue={2}
                      value={allMetric}
                      onChangeValue={value => {
                        setAllMetric(value)
                      }}
                    >
                      {_$('指定指标')}
                    </Radio>
                  </FormItem>
                  {allMetric === 2 ? (
                    <>
                      {formValues.ShieldMetrics?.map((item, index) => {
                        return (
                          <SelectMetricsTags
                            key={`${item.Metric}_${index}`}
                            index={index}
                            metric={item}
                            productType={formValues.ProductType}
                            instances={formValues.Instances}
                            metricList={metricList}
                            selectedMetrics={formValues.ShieldMetrics}
                            changeMetric={values => {
                              const cloneMetrics = cloneDeep(metricsRef.current)
                              cloneMetrics[index] = values
                              onFormChange({
                                ShieldMetrics: cloneMetrics
                              })
                              metricsRef.current = cloneMetrics
                            }}
                            addMetric={() => {
                              onFormChange({
                                ShieldMetrics: [...formValues?.ShieldMetrics, ...initShieldMetrics]
                              })
                            }}
                            removeMetric={index => {
                              const cloneMetrics = cloneDeep(formValues?.ShieldMetrics)
                              cloneMetrics.splice(index, 1)
                              onFormChange({
                                ShieldMetrics: cloneMetrics
                              })
                            }}
                          />
                        )
                      })}
                    </>
                  ) : null}
                </div>
              ) : null}
              {formValues.ShieldRange?.includes('event') ? (
                <div className="form_item_events">
                  <FormItem className="form_item_radio" label={_$('事件')}>
                    <Radio
                      name="allEvent"
                      trueValue={1}
                      value={allEvent}
                      onChangeValue={value => {
                        setAllEvent(value)
                      }}
                    >
                      {_$('全部事件')}
                    </Radio>
                    <Radio
                      name="allEvent"
                      trueValue={2}
                      value={allEvent}
                      onChangeValue={value => {
                        setAllEvent(value)
                      }}
                    >
                      {_$('指定事件')}
                    </Radio>
                  </FormItem>
                  {allEvent === 2 ? (
                    <>
                      {formValues.ShieldEvents?.map((event, index) => {
                        return (
                          <SelectEventTags
                            key={`${event.EventName}_${index}`}
                            index={index}
                            productType={formValues.ProductType}
                            event={event}
                            eventList={eventList}
                            selectedEvents={formValues.ShieldEvents}
                            changeEvent={values => {
                              const cloneEvents = cloneDeep(formValues?.ShieldEvents)
                              cloneEvents[index] = values
                              onFormChange({
                                ShieldEvents: cloneEvents
                              })
                            }}
                            addEvent={() => {
                              onFormChange({
                                ShieldEvents: [...formValues?.ShieldEvents, ...initShieldMetrics]
                              })
                            }}
                            removeEvent={index => {
                              const cloneEvents = cloneDeep(formValues?.ShieldEvents)
                              cloneEvents.splice(index, 1)
                              onFormChange({
                                ShieldEvents: cloneEvents
                              })
                            }}
                          />
                        )
                      })}
                    </>
                  ) : null}
                </div>
              ) : null}
              <FormItem
                label={_$('生效时间范围')}
                value={formValues.ShieldTimeType}
                rules={{ required: true }}
                messages={{
                  required: _$('请选择生效时间范围')
                }}
              >
                {Object.keys(ShieldTimeTypeOpts).map(key => {
                  const label = ShieldTimeTypeOpts[key]
                  return (
                    <Radio
                      name="type"
                      key={key}
                      trueValue={Number(key)}
                      value={formValues.ShieldTimeType}
                      onChangeValue={onTimeRangeChange}
                    >
                      {_$(label)}
                    </Radio>
                  )
                })}
              </FormItem>
              {formValues.ShieldTimeType === 2 ? (
                <FormItem
                  label={_$('绝对时间范围')}
                  value={formValues.AbsoluteDate}
                  rules={{ required: true }}
                  messages={{
                    required: _$('请选择绝对时间范围')
                  }}
                >
                  <Datepicker
                    range
                    format="YYYY-MM-DD HH:mm"
                    type="datetime"
                    value={formValues.AbsoluteDate}
                    disabledDate={date => {
                      return date.isBefore(dayjs().format('YYYY-MM-DD'))
                    }}
                    onChangeValue={value => {
                      if (value?.length === 2) {
                        onFormChange({
                          AbsoluteDate: value
                        })
                      }
                    }}
                  />
                </FormItem>
              ) : null}
              {formValues.ShieldTimeType === 3 ? (
                <FormItem
                  label={_$('循环时间')}
                  value={formValues?.Time}
                  rules={{ required: true }}
                  messages={{
                    required: _$('请选择循环时间')
                  }}
                >
                  <Timepicker
                    range
                    showFormat="HH:mm"
                    valueFormat="HH:mm"
                    value={formValues?.Time}
                    onChangeValue={value => {
                      if (value?.length === 2) {
                        onFormChange({
                          Time: value
                        })
                      }
                    }}
                  />
                </FormItem>
              ) : null}
            </>
          ) : null}
        </Form>
      </div>
    </Drawer>
  )
}

export default styled(EditDrawer)`
  .k-checkbox,
  .k-radio {
    margin-right: 8px;
  }
  .form_item_radio {
    margin-bottom: 16px;
  }
`
